import classNames from 'classnames'
import { AnimatePresence, motion, useReducedMotion } from 'framer-motion'
import type { KeyboardEvent } from 'react'
import NavigationArrowIcon from 'src/components/Icons/NavigationArrowIcon'
import FlexibleLink from 'src/components/ui/FlexibleLink/FlexibleLink'
import { useIsClient } from 'src/hooks/useIsClient'
import isSafeLink from 'src/utils/isSafeLink'
import { sanitizeProductCustomHtml } from 'src/utils/sanitizeProductCustomHtml'

import ImageGalleryStrip from './ImageGalleryStrip'
import type {
  TechnicalContentGalleryImage,
  TechnicalContentItem,
  TechnicalContentProduct,
  TechnicalContentTheme,
} from './types'

type RightColumnContentProps = {
  item: Pick<TechnicalContentItem, 'title' | 'description'>
  imageGallery: TechnicalContentGalleryImage[]
  product: TechnicalContentProduct
  activeIndex: number
  totalItems: number
  theme: TechnicalContentTheme
  onPrevious: () => void
  onNext: () => void
  onGoToStart: () => void
  onGoToEnd: () => void
}

const getPaginationLabel = (index: number) => {
  return String(index + 1).padStart(2, '0')
}

const getSafeHtml = (value: string) => sanitizeProductCustomHtml(value)

export default function RightColumnContent({
  item,
  imageGallery,
  product,
  activeIndex,
  totalItems,
  theme,
  onPrevious,
  onNext,
  onGoToStart,
  onGoToEnd,
}: RightColumnContentProps) {
  const isDarkTheme = theme === 'dark'
  const hasMultipleItems = totalItems > 1
  const shouldReduceMotion = useReducedMotion()
  const fadeDuration = shouldReduceMotion ? 0 : 0.2

  const isClient = useIsClient()
  const titleHtml = isClient ? getSafeHtml(item.title) : ''
  const descriptionHtml = isClient ? getSafeHtml(item.description) : ''
  const hasSafeCta = isSafeLink(product.ctaLink)

  const handleControlsKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (event.key === 'ArrowRight') {
      event.preventDefault()
      onNext()
    }

    if (event.key === 'ArrowLeft') {
      event.preventDefault()
      onPrevious()
    }

    if (event.key === 'Home') {
      event.preventDefault()
      onGoToStart()
    }

    if (event.key === 'End') {
      event.preventDefault()
      onGoToEnd()
    }
  }

  return (
    <div
      className="flex h-full flex-col px-0 md:gap-3 md:px-11"
      data-testid="technical-content-right-column"
    >
      <div
        className="flex items-center justify-between gap-4"
        data-testid="technical-content-controls"
        role="group"
      >
        <p
          className={classNames(
            'text-xl leading-none md:text-5xl font-extrabold',
            isDarkTheme ? 'text-white' : 'text-technicalContent20Muted'
          )}
          data-testid="technical-content-pagination-index"
          aria-live="polite"
          aria-atomic="true"
        >
          {getPaginationLabel(activeIndex)}
        </p>

        <div className="flex items-center gap-2">
          <button
            type="button"
            aria-label="Item anterior"
            className={classNames(
              'flex h-10 w-10 items-center justify-center rounded-round border text-heading-xs leading-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-restructure-focus-primary md:h-8 md:w-8',
              isDarkTheme
                ? 'border-technicalContent20BorderInverted bg-technicalContent20CtaBg text-white hover:bg-technicalContent20CtaHover'
                : 'border-technicalContent20Border bg-white text-black hover:bg-restructure-background-neutral-2',
              !hasMultipleItems &&
                'cursor-not-allowed opacity-50 hover:bg-transparent'
            )}
            onClick={onPrevious}
            onKeyDown={handleControlsKeyDown}
            data-testid="technical-content-prev-button"
            disabled={!hasMultipleItems}
          >
            <NavigationArrowIcon
              direction="left"
              strokeColor={isDarkTheme ? 'white' : 'black'}
            />
          </button>

          <button
            type="button"
            aria-label="Próximo item"
            className={classNames(
              'flex h-10 w-10 items-center justify-center rounded-round border text-heading-xs leading-none transition-colors focus:outline-none focus-visible:ring-2 focus-visible:ring-restructure-focus-primary md:h-8 md:w-8',
              isDarkTheme
                ? 'border-technicalContent20BorderInverted bg-white text-black hover:bg-restructure-background-neutral-2'
                : 'border-technicalContent20Border bg-technicalContent20CtaBg text-white hover:bg-technicalContent20CtaHover',
              !hasMultipleItems &&
                'cursor-not-allowed opacity-50 hover:bg-transparent'
            )}
            onClick={onNext}
            onKeyDown={handleControlsKeyDown}
            data-testid="technical-content-next-button"
            disabled={!hasMultipleItems}
          >
            <NavigationArrowIcon
              direction="right"
              strokeColor={isDarkTheme ? 'black' : 'white'}
            />
          </button>
        </div>
      </div>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`technical-content-title-${activeIndex}`}
          className={classNames(
            'text-xl uppercase font-medium md:text-4xl max-w-[225px] md:max-w-[510px]',
            isDarkTheme ? 'text-white' : 'text-fullDark'
          )}
          data-testid="technical-content-title"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: fadeDuration, ease: 'easeInOut' }}
        >
          {titleHtml ? (
            <span dangerouslySetInnerHTML={{ __html: titleHtml }} />
          ) : (
            item.title
          )}
        </motion.div>
      </AnimatePresence>

      <AnimatePresence mode="wait" initial={false}>
        <motion.div
          key={`technical-content-description-${activeIndex}`}
          className={classNames(
            'text-sm md:text-base max-w-[510px]',
            isDarkTheme
              ? 'text-technicalContent20BodyInverted'
              : 'text-technicalContent20Body'
          )}
          data-testid="technical-content-description"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: fadeDuration, ease: 'easeInOut' }}
        >
          {descriptionHtml ? (
            <span dangerouslySetInnerHTML={{ __html: descriptionHtml }} />
          ) : (
            item.description
          )}
        </motion.div>
      </AnimatePresence>

      <ImageGalleryStrip images={imageGallery} />

      <div
        className={classNames(
          'mt-3 flex items-center gap-[49px]',
          isDarkTheme
            ? 'border-technicalContent20BorderInverted'
            : 'border-technicalContent20Border'
        )}
      >
        <p
          className={classNames(
            'font-extrabold text-[18px] uppercase',
            isDarkTheme ? 'text-white' : 'text-fullDark'
          )}
          data-testid="technical-content-product-name"
        >
          {product.productName}
        </p>

        {hasSafeCta && (
          <FlexibleLink
            linkUrl={product.ctaLink}
            label={product.ctaText}
            classNames={classNames(
              'inline-flex min-h-[43px] items-center font-bold justify-center rounded-round px-[26px] py-2 text-sm transition-colors',
              isDarkTheme
                ? 'bg-white text-darkThemeComponents'
                : 'bg-technicalContent20CtaBg text-white'
            )}
          />
        )}
      </div>
    </div>
  )
}
